/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker 
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ product_form_id }}'
      currency='{{ cart.currency.iso_code }}'
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: stretch;
    width: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    min-inline-size: 0;
  }
  .skio-plan-picker__subscriptions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    width: 100%;
  }
  .skio-plan-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    width: 100%;
  }
  .skio-plan-card-wrap {
    position: relative;
    width: 100%;
  }
  .skio-plan-popular {
    position: absolute;
    top: -10px;
    right: 20px;
    padding: 4px 10px;
    border-radius: 4px;
    background: #f93;
    color: #fff;
    font-size: 12px;
    line-height: normal;
    font-weight: 600;
    letter-spacing: 0.48px;
    white-space: nowrap;
    z-index: 1;
    pointer-events: none;
  }
  .skio-plan-card {
    width: 100%;
    border: 2px solid transparent;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    color: #34271e;
    cursor: pointer;
    padding: 16px 20px 14px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: stretch;
    text-align: left;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
    font: inherit;
  }
  .skio-plan-card--selected {
    border-color: #f93;
    background: #fffbf5;
    box-shadow: none;
  }
  .skio-plan-card:focus-visible {
    outline: 2px solid #f93;
    outline-offset: 2px;
  }
  .skio-plan-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 50px;
  }
  .skio-plan-card__left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
  }
  .skio-plan-card__radio {
    width: 18px;
    height: 18px;
    border: 2px solid #d9d9d4;
    border-radius: 50%;
    box-sizing: border-box;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    background: #fff;
  }
  .skio-plan-card--selected .skio-plan-card__radio {
    border-color: #f93;
  }
  .skio-plan-card--selected .skio-plan-card__radio::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f93;
  }
  .skio-plan-card__label-col {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
  .skio-plan-title {
    margin: 0;
    font-size: 16px;
    line-height: normal;
    font-weight: 600;
    color: #34271e;
  }
  .skio-plan-title-save {
    color: #f93;
    font-weight: 600;
  }
  .skio-plan-subtitle {
    margin: 0;
    font-size: 12px;
    line-height: normal;
    font-weight: 400;
    color: #80736b;
    white-space: nowrap;
  }
  .skio-plan-card__price-col {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
  }
  .skio-plan-price {
    margin: 0;
    font-size: 0;
    line-height: normal;
    font-weight: 600;
    color: #f93;
    white-space: nowrap;
  }
  .skio-plan-price-current {
    font-size: 20px;
    font-weight: 600;
    color: #f93;
  }
  .skio-plan-price-compare {
    margin-left: 4px;
    font-size: 15px;
    font-weight: 400;
    color: #918b85;
    text-decoration: line-through;
  }
  .skio-plan-price-unit {
    margin: 0;
    font-size: 12px;
    line-height: normal;
    font-weight: 400;
    color: #918b85;
    white-space: nowrap;
  }
  .skio-plan-card-wrap .skio-plan-card__divider {
    display: none;
  }
  .skio-plan-billed {
    margin: 0;
    padding-left: 30px;
    font-size: 12px;
    line-height: normal;
    font-weight: 400;
    color: #80736b;
    white-space: nowrap;
  }
  .skio-plan-onetime-wrap {
    margin-top: 0;
    width: 100%;
  }
  .skio-plan-onetime {
    width: 100%;
    min-height: 50px;
    border: 2px solid transparent;
    border-radius: 6px;
    background: #fff;
    color: #1a1a1a;
    cursor: pointer;
    padding: 15px 17px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    gap: 12px;
    box-sizing: border-box;
    font: inherit;
    box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  }
  .skio-plan-onetime:not(.skio-plan-onetime--selected) {
    border-color: transparent;
    background: #fff;
  }
  .skio-plan-onetime--selected {
    border-color: #f93;
    background: #fffbf5;
    box-shadow: none;
  }
  .skio-plan-onetime:focus-visible {
    outline: 2px solid #f93;
    outline-offset: 2px;
  }
  .skio-plan-onetime__row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .skio-plan-onetime__radio {
    width: 18px;
    height: 18px;
    border: 2px solid #d9d9d4;
    border-radius: 50%;
    box-sizing: border-box;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    background: #fff;
  }
  .skio-plan-onetime--selected .skio-plan-onetime__radio {
    border-color: #f93;
  }
  .skio-plan-onetime--selected .skio-plan-onetime__radio::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f93;
  }
  .skio-plan-onetime__label {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    color: #1a1a1a;
  }
  .skio-plan-onetime__price {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    white-space: nowrap;
    flex-shrink: 0;
    color: #1a1a1a;
  }
  ::slotted(.skio-subscribe-footer-note) {
    display: block;
    width: 100%;
    margin: 0;
    font-size: 12px;
    line-height: normal;
    font-weight: 400;
    color: #80736b;
    text-align: center;
  }
  @media (max-width: 767px) {
    .skio-plan-picker {
      gap: 16px;
    }
    .skio-plan-options {
      gap: 16px;
    }
    .skio-plan-popular {
      top: -12px;
      right: 12px;
      left: auto;
      font-size: 12px;
      letter-spacing: 0.48px;
      padding: 4px 10px;
    }
    .skio-plan-card {
      padding: 16px 16px 14px;
      gap: 4px;
      border-width: 1px;
    }
    .skio-plan-card:not(.skio-plan-card--selected) {
      box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    }
    .skio-plan-card--selected {
      box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    }
    .skio-plan-card__top {
      min-height: 0;
      align-items: flex-start;
    }
    .skio-plan-card__left {
      align-items: center;
    }
    .skio-plan-card__label-col {
      max-width: 11rem;
    }
    .skio-plan-title {
      display: flex;
      flex-wrap: wrap;
      column-gap: 4px;
      row-gap: 0;
      font-size: 14px;
      line-height: normal;
    }
    .skio-plan-title-save {
      font-size: 14px;
    }
    .skio-plan-subtitle {
      font-size: 10px;
    }
    .skio-plan-price-current {
      font-size: 16px;
    }
    .skio-plan-price-compare {
      font-size: 14px;
    }
    .skio-plan-price-unit {
      font-size: 10px;
    }
    .skio-plan-billed {
      font-size: 10px;
      padding-left: 30px;
    }
    .skio-plan-onetime {
      min-height: 48px;
      padding: 14px 16px;
      border-width: 1px;
    }
    .skio-plan-onetime--selected {
      box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    }
    .skio-plan-onetime__label,
    .skio-plan-onetime__price {
      font-size: 14px;
      line-height: 20px;
    }
  }

  button.add-to-cart {
    justify-content: center;
    font-weight: 400;
    text-transform: capitalize;
    letter-spacing: 0;
    font-style: normal;
    font-size: 16px;
    text-decoration: none;
    box-shadow: 0 0 #0000004d inset;
    width: 100%;
    color: #fff;
    background-color: #000;
    border-radius: 5px;
    transition-duration: .3s;
    padding: 10px 32px;
    text-transform: uppercase;
    cursor: pointer;
    font-family: 'FK Grotesk Neue Trial';
    transition: border-color 0.25s ease, background-color 0.25s ease, color 0.25s ease;

    border: solid 2px rgba(0,0,0,0);
  }
  button.add-to-cart:hover {
    background-color: rgba(0,0,0,0);
    color: #000;
    border-color: #000;
  }

  .skio-details {
    --text-color: #333;
    --text-color-secondary: #888; 
    
    user-select: none;
    -webkit-user-select: none;
    margin-bottom: 20px;
    order: 3;
  }

  .skio-details summary::-webkit-details-marker,
  .skio-details summary::marker,
  .skio-details slot {
    color: rgba(0,0,0,0) !important;
  }

  .skio-details summary {
    margin-top: 15px;
  }

  .skio-details summary span {
    font-size: 0.9em;
    display: flex;
    padding: .5em 0;
    cursor: pointer;
    align-items: center;
    gap: 10px;
    text-decoration: underline;

    margin-top: -40px;
    color: #000;
  }

  @keyframes fadeInDown {
    0% {
      opacity: 0;
      transform: translateY(-15px);
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
    }
  }
  .skio-details[open] > .skio-details--content {
    animation-name: fadeInDown;
    animation-duration: 0.3s;
  }

  .skio-details--content {
    position: absolute;
    z-index: 1020;
    padding: 1em;
    width: fit-content;
    border-radius: 5px;
    background: white;
    box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
  }

  .skio-details ul {
    margin: 0;
    padding: 0;
  }

  .skio-details ul li {
    display: flex;
    align-items: flex-start;
    gap: .75em;

    margin-bottom: 1em;
  }

  .skio-details .skio-content {
    display: flex;
    flex-direction: column;
  }

  .skio-details .skio-content p {
    font-size: 0.9em;

    margin-top: 0;
    margin-bottom: 0;

    letter-spacing: 0;
    line-height: 1.5;

    color: var(--text-color);
  }

  .skio-details ul li small {
    font-size: 0.7em;
    color: var(--text-color-secondary);
  }

  .skio-details .skio-icon {
    display: flex;

    width: 2.25em;
    height: 2.25em;

    color: var(--text-color);
    background: #f8f8f8;
    border-radius: 100%;

    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }

  .skio-details .skio-icon svg {
    width: 1.25em;
    height: 1.25em;

    color: inherit;
  }

  .skio-details--footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.9em;
  }

  .skio-details--footer a {
    color: var(--text-color);
  }

  .skio-manage-link {
    text-decoration: underline;
  }

  .powered-by-skio {
    font-size: 0.8em;

    display: flex;
    text-decoration: none;
    
    align-items: center;
    gap: 3px;
  }

`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },            //required
    productHandle: { type: String },      //optional (unless product isn't passed, then required)
    key: { type: String },                //optional, defaults to product.id; identifier for this instance of the Skio plan picker
    
    formId: { type: String },             //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean },       //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String },     //optional, defaults to percent; can also pass "fixed"
    
    currency: { type: String },           //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String },           //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {},                   //placeholder for object

    externalPriceSelector: { type: String },      //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String },      //optional, used to update the external price
  
    selectedVariant: { type: Object },    //placeholder for data
    skioSellingPlanGroups: {},            //placeholder for data
    availableSellingPlanGroups: {},       //placeholder for data
    selectedSellingPlanGroup: {},         //placeholder for data
    selectedSellingPlan: {},              //placeholder for data

    defaultFrequency: {},                 //placeholder for data
    defaultPlanIndex: { type: Number },

    showAddToCartButton: { type: Boolean },

    meta: {},

    useVariantInputClickEvents: {type: Boolean},
    variantInputSelector: {},

    purchaseOption: { type: String }
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = 'subscription';

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = true;
    this.subscriptionFirst = true;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.externalPriceSelector = '[skio-external-price]';

    this.externalPriceSelectorWithCurrency = '[skio-external-price-with-currency]';

    this.currency =
      (typeof Shopify !== 'undefined' && Shopify.currency?.active) ||
      this.currency ||
      'USD';
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;
    this.defaultPlanIndex = null;

    this.showAddToCartButton = false;
    this._skioFormSubmitBound = false;

    this.meta = '';

    this.lastSellingPlanName = '';

    this.showDetailsHover = false;

    this.useVariantInputClickEvents = null;
    this.variantInputSelector = null;

  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    if(!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
        this._bindProductFormQuantitySync();
      }
    }
    let skio = this;
    document.addEventListener("variantChanged", function(e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find(x => x.id == variantId);
      if (variant) skio.selectedVariant = variant;
      else skio.log("Unable to find variant with id: ", variantId);
      skio.requestUpdate();
    });

    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: skio.currency,
    });

    if (this.meta !== '' && this.meta != null) {
      try {
        this.meta = JSON.parse(this.meta);
      } catch (e) {
        this.meta = {};
      }
    }

    if (this.useVariantInputClickEvents) {
      document.addEventListener('load', skio.addVariantClickEventListeners)
    }

  }

  render() {
    if(!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0 || !this.product?.available) return;
    const primaryGroup = this.availableSellingPlanGroups?.[0];
    const planCards = primaryGroup?.selling_plans || [];
    const popularIndex = this.getPopularPlanIndex(planCards);
    const allowOneTime = !this.product.requires_selling_plan;
    const isOneTime = allowOneTime && this.purchaseOption === 'onetime';

    return html`
      <fieldset class="skio-plan-picker" skio-plan-picker="${ this.key }">
        <input ${ this.formId !== null ? html`form="${ this.formId }"` : '' } name="selling_plan" type="hidden" value="${ this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}" />
        <input ${ this.formId !== null ? html`form="${ this.formId }"` : '' } name="properties[Discount]" type="hidden" value="${ this.selectedSellingPlan !== null ? this.discount(this.selectedSellingPlan).percent : '' }" 
          ?disabled="${ this.selectedSellingPlan == null ? true : false }" />
        
        ${ planCards.length > 0 ? html`
          <div class="skio-plan-picker__subscriptions">
            <div class="skio-plan-options">
              ${ planCards.map((plan, index) => {
                const cardMeta = this.planCardCopy(plan);
                const isSelected = this.selectedSellingPlan?.id == plan.id;
                const isPopular = popularIndex !== null && index === popularIndex;

                return html`
                  <div class="skio-plan-card-wrap">
                    ${ isPopular ? html`<div class="skio-plan-popular">Most Popular</div>` : '' }
                    <button
                      type="button"
                      class="skio-plan-card ${ isSelected ? 'skio-plan-card--selected' : '' }"
                      @click=${() => this.selectSellingPlanButton(plan, primaryGroup) }
                      aria-pressed="${ isSelected ? 'true' : 'false' }"
                    >
                      <div class="skio-plan-card__top">
                        <div class="skio-plan-card__left">
                          <span class="skio-plan-card__radio" aria-hidden="true"></span>
                          <div class="skio-plan-card__label-col">
                            <p class="skio-plan-title">
                              ${ cardMeta.title }
                              ${ cardMeta.saveLabel ? html`<span class="skio-plan-title-save"> ${ cardMeta.saveLabel }</span>` : '' }
                            </p>
                            <p class="skio-plan-subtitle">${ cardMeta.sessionsSubtitle }</p>
                          </div>
                        </div>
                        <div class="skio-plan-card__price-col">
                          <p class="skio-plan-price">
                            <span class="skio-plan-price-current">${ this.planPerBundlePrice(plan) }</span>
                            <span class="skio-plan-price-compare">${ this.planCompareAtPrice() }</span>
                          </p>
                          <p class="skio-plan-price-unit">per bundle</p>
                        </div>
                      </div>
                      <hr class="skio-plan-card__divider" aria-hidden="true" />
                      <p class="skio-plan-billed">${ this.planBilledText(plan) }</p>
                    </button>
                  </div>
                `;
              })}
            </div>
            <slot name="footer"></slot>
          </div>
          ${ allowOneTime ? html`
            <div class="skio-plan-onetime-wrap">
              <button
                type="button"
                class="skio-plan-onetime ${ isOneTime ? 'skio-plan-onetime--selected' : '' }"
                @click=${ () => this.selectOneTimePurchase() }
                aria-pressed="${ isOneTime ? 'true' : 'false' }"
                aria-label="One-time purchase, ${ this.money(this.selectedVariant.price) }"
              >
                <span class="skio-plan-onetime__row">
                  <span class="skio-plan-onetime__radio" aria-hidden="true"></span>
                  <span class="skio-plan-onetime__label">One-time</span>
                </span>
                <span class="skio-plan-onetime__price">${ this.money(this.selectedVariant.price) }</span>
              </button>
            </div>
          ` : '' }
        ` : '' }

                        <details class="skio-details" @mouseover=${ (e) => this.detailsMouseover() } @mouseleave=${ (e) => this.detailsMouseleave() }>
              <summary>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ai ai-ArrowRepeat"><path d="M18 2l3 3-3 3"/><path d="M6 22l-3-3 3-3"/><path d="M21 5H10a7 7 0 0 0-7 7"/><path d="M3 19h11a7 7 0 0 0 7-7"/></svg>
                
                  How do subscriptions work?
                </span>
              </summary>
              <div class="skio-details--content">
                <ul>
                  <li>
                    <div class="skio-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div class="skio-content">
                      <p>Get exclusive deals</p>
                      <small>Subscribe for unique discounts</small>
                    </div>
                  </li>
                  <li>
                    <div class="skio-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </div>
                    <div class="skio-content">
                      <p>Edit your subscription anytime</p>
                      <small>Edit products, delivery schedule and more</small>
                    </div>
                  </li>
                  <li>
                    <div class="skio-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    
                    <div class="skio-content">
                      <p>No commitment</p>
                      <small>Easy to cancel if it’s not for you</small>
                    </div>
                  </li>
                </ul>
                <div class="skio-details--footer">
                  <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a> 
                  
                  <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
                    Powered by
                    <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z" fill="black"/>
                      <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black"/>
                      <path d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z" fill="black"/>
                      <path d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z" fill="black"/>
                    </svg>
                  </a>
                  
                </div>
              </div>
            </details>

            ${ this.showAddToCartButton ? html`
              <button @click=${() => this.addToCart() } class="add-to-cart">Add to Cart</button>
            ` : html``}

      </fieldset>`
  }

  updated = (changed) => {
    if(changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this._filterSkioSellingPlanGroups(
        this.product.selling_plan_groups
      );

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function(a,b){
          if (parseInt(a.name.replace(/\D/g, "")) < parseInt(b.name.replace(/\D/g, ""))) return -1;
          if (parseInt(a.name.replace(/\D/g, "")) > parseInt(b.name.replace(/\D/g, ""))) return 1;
          if (parseInt(a.name.replace(/\D/g, "")) == parseInt(b.name.replace(/\D/g, ""))) return 0;
        })
      });

    }

    if (this.selectedVariant && (changed.has('selectedVariant') || changed.has('product'))) {
      let skioSellingPlanGroups = JSON.parse(JSON.stringify(this.skioSellingPlanGroups));
      this.availableSellingPlanGroups = skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      );

      if (this.availableSellingPlanGroups?.length > 0) {
        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter(x =>
            this.selectedVariant.selling_plan_allocations.find(y => y.selling_plan_id == x.id)
          );
        });

        const persistedPlanId = this.readPersistedSellingPlanId();
        const prevPlan = this.selectedSellingPlan;
        const defaultIdx = this.getEffectiveDefaultPlanIndex();

        this.availableSellingPlanGroups.forEach((group) => {
          if (!group.selling_plans.length) return;
          let plan = null;
          if (persistedPlanId) {
            plan = group.selling_plans.find((p) => String(p.id) === String(persistedPlanId));
          }
          if (!plan && prevPlan) {
            plan = group.selling_plans.find((p) => p.id === prevPlan.id);
          }
          if (!plan && prevPlan?.name) {
            plan = group.selling_plans.find((p) => p.name === prevPlan.name);
          }
          if (!plan && this.defaultFrequency) {
            plan = group.selling_plans.find((x) => x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()));
          }
          if (!plan && defaultIdx !== null && group.selling_plans[defaultIdx]) {
            plan = group.selling_plans[defaultIdx];
          }
          group.selected_selling_plan = plan || group.selling_plans[0];
        });

        const wantsOnetime =
          !this.product.requires_selling_plan &&
          (this.readPersistedPurchaseMode() === 'onetime' || this.purchaseOption === 'onetime');

        if (this.product.requires_selling_plan == true) {
          this.clearPersistedPurchaseMode();
          const primary = this.availableSellingPlanGroups[0];
          const resolved = primary?.selected_selling_plan ?? primary?.selling_plans?.[0];
          if (resolved) {
            this.selectedSellingPlanGroup = primary;
            this.selectedSellingPlan = resolved;
            this.lastSellingPlanName = resolved.name;
            this.purchaseOption = 'subscription';
          } else {
            this.selectedSellingPlan = null;
            this.selectedSellingPlanGroup = null;
          }
        } else if (wantsOnetime) {
          this.purchaseOption = 'onetime';
          this.selectedSellingPlan = null;
          this.selectedSellingPlanGroup = null;
        } else if (this.startSubscription == true || this.purchaseOption == 'subscription') {
          const primary = this.availableSellingPlanGroups[0];
          const resolved = primary?.selected_selling_plan ?? primary?.selling_plans?.[0];
          if (resolved) {
            this.selectedSellingPlanGroup = primary;
            this.selectedSellingPlan = resolved;
            this.lastSellingPlanName = resolved.name;
          } else {
            this.selectedSellingPlan = null;
            this.selectedSellingPlanGroup = null;
          }
        } else {
          this.selectedSellingPlan = null;
          this.selectedSellingPlanGroup = null;
        }
      } else {
        this.selectedSellingPlan = null;
        this.selectedSellingPlanGroup = null;
      }

      this.updateForm();
    }

    if(changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${ this.key }"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan == null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true, 
        composed: true, 
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();
      this.updateExternalPrice();

    }

    if(changed.has('formId')) {
      this.updateForm();
      this._bindProductFormQuantitySync();
    }

    if (
      changed.has('selectedSellingPlan') ||
      changed.has('purchaseOption') ||
      changed.has('selectedVariant')
    ) {
      requestAnimationFrame(() => {
        if (this.formId) {
          const form = document.getElementById(this.formId);
          if (form) this.syncCartQuantityToForm(form);
        }
      });
    }

  }

  firstUpdated() {
    this._bindProductFormQuantitySync();
    if (this.formId) {
      const form = document.getElementById(this.formId);
      if (form) this.syncCartQuantityToForm(form);
    }
  }

  _bindProductFormQuantitySync() {
    if (!this.formId || this._skioFormSubmitBound) return;
    const form = document.getElementById(this.formId);
    if (!form) return;
    this._skioFormSubmitBound = true;
    form.addEventListener('submit', () => {
      this.syncCartQuantityToForm(form);
    });
  }

  syncCartQuantityToForm(form) {
    if (!form) return;
    this.syncQuantityFromSkioToProductForm(form);
  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  error = (...args) =>  {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details');
    let summary = this.renderRoot.querySelector('.skio-details summary');
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click();
      this.showDetailsHover = true;
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details');
    let summary = this.renderRoot.querySelector('.skio-details summary');
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click();
      this.showDetailsHover = false;
    }
  }

  updatePriceElement() {
    let priceEl = document.querySelector(`[data-skio-price="${ this.product.id }"]`);
    if (priceEl) {
      //add formatted price here
      let high = 0;
      this.product.variants.forEach((variant) => {
        if (variant.price > high) high = variant.price;
      });
      let content = '';
      if (!this.selectedSellingPlan) {
        content = this.moneyFormatter.format(this.selectedVariant.price / 100).replaceAll('A', '');
      } else {
        content = `<del>${this.moneyFormatter.format(high / 100).replaceAll('A', '')}</del>${this.price(this.selectedSellingPlan).replaceAll('A', '')}`;
      }
      priceEl.innerHTML = content;
    }
  }

  getMetadata(field) {
    let meta = this.meta;
    if (meta !== {}){
      let fieldData = meta[field];
      if (fieldData) return fieldData;
      else return '';
    }
  }

  getVariantMetadata(variantId, field) {
    let meta = this.meta;
    if (meta !== {}){
      let variantData = meta[variantId];
      if (variantData) {
        let fieldData = variantData[field];
        if (fieldData) return fieldData;
        else return '';
      }
    }
  }

  updateExternalPrice() {
    document.querySelectorAll(this.externalPriceSelector).forEach((el) => {
      this.selectedSellingPlan ? el.innerHTML = this.price(this.selectedSellingPlan) : el.innerHTML = this.money( this.selectedVariant.price);
    })
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      this.selectedSellingPlan ? el.innerHTML = this.price(this.selectedSellingPlan) + ' ' + this.currency : el.innerHTML = this.money( this.selectedVariant.price) + ' ' + this.currency;
    })
  }

  _filterSkioSellingPlanGroups(groups) {
    if (!Array.isArray(groups)) return [];
    return groups.filter((group) => String(group?.app_id || '').toUpperCase() === 'SKIO');
  }

  getBundleCountFromPlan(plan) {
    if (!plan) return null;
    const valueSource = plan?.options?.[0]?.value || plan?.name || '';
    const matchedNumber = parseInt(String(valueSource).match(/\d+/)?.[0] || '', 10);
    if (!matchedNumber || Number.isNaN(matchedNumber)) return null;
    return matchedNumber;
  }

  getEffectiveDefaultPlanIndex() {
    if (this.defaultPlanIndex == null || this.defaultPlanIndex === '') return null;
    const n = parseInt(String(this.defaultPlanIndex), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  getPopularPlanIndex(planCards) {
    const defaultIdx = this.getEffectiveDefaultPlanIndex();
    if (defaultIdx === null || !planCards?.length) return null;
    return defaultIdx >= 0 && defaultIdx < planCards.length ? defaultIdx : null;
  }

  skioPersistStorageKey() {
    const pid = this.product?.id ?? this.key ?? 'product';
    return `skio-plan-choice:${pid}`;
  }

  skioPurchaseModeStorageKey() {
    const pid = this.product?.id ?? this.key ?? 'product';
    return `skio-purchase-mode:${pid}`;
  }

  readPersistedPurchaseMode() {
    try {
      return sessionStorage.getItem(this.skioPurchaseModeStorageKey());
    } catch (e) {
      return null;
    }
  }

  persistOnetimePurchaseMode() {
    try {
      sessionStorage.setItem(this.skioPurchaseModeStorageKey(), 'onetime');
    } catch (e) {}
  }

  clearPersistedPurchaseMode() {
    try {
      sessionStorage.removeItem(this.skioPurchaseModeStorageKey());
    } catch (e) {}
  }

  readPersistedSellingPlanId() {
    try {
      return sessionStorage.getItem(this.skioPersistStorageKey());
    } catch (e) {
      return null;
    }
  }

  persistSellingPlanChoice(plan) {
    if (!plan?.id) return;
    try {
      sessionStorage.setItem(this.skioPersistStorageKey(), String(plan.id));
      this.clearPersistedPurchaseMode();
    } catch (e) {}
  }

  _subscriptionDiscountIsActive() {
    if (this.purchaseOption === 'onetime' || !this.selectedSellingPlan) return false;
    const percent = this.discount(this.selectedSellingPlan)?.percent;
    if (!percent || percent === '0%') return false;
    const n = parseFloat(String(percent).replace(/%/g, '').replace(/,/g, '.'));
    return Number.isFinite(n) && n > 0;
  }

  cartQuantityForSelectedPlan() {
    if (!this._subscriptionDiscountIsActive()) return 1;
    const n = this.getBundleCountFromPlan(this.selectedSellingPlan);
    return n != null ? n : 1;
  }

  planCardCopy(plan) {
    const matchedNumber = this.getBundleCountFromPlan(plan);
    const savePercent = this.discount(plan)?.percent;
    const saveLabel = savePercent && savePercent !== '0%'
      ? `(Save ${savePercent})`
      : '';

    if (!matchedNumber) {
      return {
        title: plan.name,
        saveLabel,
        sessionsSubtitle: 'Delivered monthly'
      };
    }

    const sessions = matchedNumber * 4;
    const bundleLabel = matchedNumber === 1 ? 'bundle' : 'bundles';
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches;

    return {
      title: `${matchedNumber} ${bundleLabel}/month`,
      saveLabel,
      sessionsSubtitle: isMobile
        ? `${matchedNumber} ${bundleLabel} delivered monthly`
        : `~ ${sessions} sessions/month`
    };
  }

  planPerBundlePrice(plan) {
    const bundleQty = this.getBundleCountFromPlan(plan) || 1;
    const totalCents = this.price(plan, false);
    return this.money(Math.round(totalCents / bundleQty));
  }

  planCompareAtPrice() {
    return this.money(this.selectedVariant.price);
  }

  planBilledText(plan) {
    return `Billed ${this.price(plan)} every month`;
  }

  _getPriceAdjustment(selling_plan) {
    const adjustments = selling_plan?.price_adjustments;
    if (!Array.isArray(adjustments) || !adjustments.length) return null;
    return adjustments[0];
  }
  
  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (this.selectedSellingPlan) this.lastSellingPlanName = this.selectedSellingPlan.name;
    if (group) {
      this.purchaseOption = 'subscription';
    } else {
      this.purchaseOption = 'onetime';
      try {
        sessionStorage.removeItem(this.skioPersistStorageKey());
      } catch (e) {}
      this.persistOnetimePurchaseMode();
    }

    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find(x => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlanButton(plan, group) {
    let selling_plan = group.selling_plans.find(x => x.id == plan.id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
      this.purchaseOption = 'subscription';
      this.persistSellingPlanChoice(selling_plan);
      this.updateForm();
      this.updateExternalPrice();
      this.requestUpdate();
    }
    else this.log("Error: couldn't find selling plan with id " + plan.id + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  selectOneTimePurchase() {
    if (this.product?.requires_selling_plan) return;
    try {
      sessionStorage.removeItem(this.skioPersistStorageKey());
    } catch (e) {}
    this.persistOnetimePurchaseMode();
    this.purchaseOption = 'onetime';
    this.selectedSellingPlan = null;
    this.selectedSellingPlanGroup = null;
    this.updateForm();
    this.updateExternalPrice();
    this.requestUpdate();
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  _planPriceMultiplier(selling_plan) {
    if (!selling_plan) return 1;
    if (selling_plan.name?.includes('prepaid')) {
      const option = selling_plan.options?.[0]?.value;
      const match = option && String(option).match(/\d+/g);
      const n = match ? parseInt(match.join(''), 10) : NaN;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }
    return this.getBundleCountFromPlan(selling_plan) || 1;
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    const empty = { percent: '0%', amount: 0 };
    if (!selling_plan || !this.selectedVariant?.price) return empty;

    const price_adjustment = this._getPriceAdjustment(selling_plan);
    if (!price_adjustment?.value_type) return empty;

    const multiplier = this._planPriceMultiplier(selling_plan);
    const price = this.selectedVariant.price * multiplier;
    if (!price) return empty;

    const discount = { percent: '0%', amount: 0 };

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`;
        discount.amount = Math.round((price * price_adjustment.value) / 100.0);
        break;
      case 'fixed_amount':
        discount.percent = `${Math.round(((price_adjustment.value * 1.0) / price) * 100.0)}%`;
        discount.amount = selling_plan.name?.includes('prepaid')
          ? price - price_adjustment.value
          : price_adjustment.value;
        break;
      case 'price':
        discount.percent = `${Math.round((((price - price_adjustment.value) * 1.0) / price) * 100.0)}%`;
        discount.amount = price - price_adjustment.value;
        break;
    }

    return discount;
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    if (!selling_plan || !this.selectedVariant?.price) {
      return formatted ? this.money(0) : 0;
    }

    const multiplier = this._planPriceMultiplier(selling_plan);
    const totalCents =
      this.selectedVariant.price * multiplier - this.discount(selling_plan).amount;

    return formatted ? this.money(totalCents) : totalCents;
  }

  _setProductFormQuantity(form, n) {
    const qty = Math.max(1, Math.min(999, Math.floor(Number(n)) || 1));
    let inp = form.querySelector("input[name='quantity']");
    if (!inp) {
      inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = "quantity";
      form.appendChild(inp);
    }
    inp.value = String(qty);
    inp.dispatchEvent(new Event("input", { bubbles: true }));
    inp.dispatchEvent(new Event("change", { bubbles: true }));
  }

  syncQuantityFromSkioToProductForm(form) {
    if (!form) return;
    this._setProductFormQuantity(form, this.cartQuantityForSelectedPlan());
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.getElementById(this.formId) || document.querySelector(`#${this.formId}`);

      if (form) {
        let selling_plan_input = form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement('input');
          selling_plan_input.type = "hidden";
          selling_plan_input.name = "selling_plan";
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }

        let discountValue = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
        if (discountValue == '0%') discountValue = null;

        let discount_input = form.querySelector('[name="properties[Discount]"]');
        if (discount_input) {
          discount_input.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          if (discountValue == null) discount_input.disabled = true;
        } else {
          discount_input = document.createElement('input');
          discount_input.type = "hidden";
          discount_input.name = "properties[Discount]";
          discount_input.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          if (discountValue == null) discount_input.disabled = true;
          form.append(discount_input);
        }

        this.syncQuantityFromSkioToProductForm(form);

      } else {
        console.log(`Skio error: form ID is ${ this.formId }, but no form with that ID was found.`);
      }
    }
  }

  addVariantClickEventListeners() {
    let variantInputs = document.querySelectorAll(this.variantInputSelector)
    let skio = this
    for (let el of variantInputs) {
      el.addEventListener('click', function(e) {
        // may need to replace with ID / e.target depending on client setup
        // may need to use different attribute depending on ^^
        let variantTitle = e.currentTarget.value
        skio.selectedVariant = skio.product.variants.find(variant => variant.title == variantTitle)
      })
    }
  }

  // Optional functions keep if necessary 

  /**
   *   
   * 
   */
  
  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity = 1) {
    let q = quantity;
    if (this.formId) {
      const form = document.getElementById(this.formId);
      const qi = form && form.querySelector('input[name="quantity"]');
      if (qi && qi.value !== "") {
        const parsed = parseInt(qi.value, 10);
        if (Number.isFinite(parsed) && parsed >= 1) q = parsed;
      }
    }
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: q,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then((response) => response.json())
    .then((response) => {
      this.log("SKIO added item to cart: ", response);
      //dispatch CustomEvent to tell document that an item was added to cart
      const event = new CustomEvent(`skio::added-to-cart`, {
        bubbles: true, 
        composed: true, 
        detail: {
          response,
          key: this.key
        }
      });

      this.dispatchEvent(event);
    })
    .catch((error) => {
      this.error(`SKIO ${ this.key } error adding item to cart: `, error);
    });
  } 

  fetchProduct = (handle) => {
    return fetch(`/products/${ handle }.js`)
    .then((response) => response.json())
    .then((product) => {
      this.product = product;
      this.selectedVariant = product.variants[0];

      return product;
    });
  }
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);